// src/pages/admin/User_pcp.jsx
import { useEffect, useMemo, useState } from "react";
import { listCompanies } from "../../api/users/companies";
import {
  listRoles,
  createRole,
  updateRole,
  deleteRole,
} from "../../api/users/roles";
import { listPCP, createPCP, deletePCP } from "../../api/users/pcp";

const EMPTY_ROLE = { code: "", name: "" };
const EMPTY_PCP = {
  company: "",
  participant_role: "",
  is_lead: false,
  start_date: "",
  end_date: "",
};

const User_pcp = () => {
  const projectId = useMemo(() => localStorage.getItem("projectId"), []);
  const [loading, setLoading] = useState(false);

  // 옵션 데이터
  const [companies, setCompanies] = useState([]);
  const [roles, setRoles] = useState([]);

  // PCP 목록
  const [pcpRows, setPcpRows] = useState([]);

  // 역할 폼
  const [roleForm, setRoleForm] = useState({ ...EMPTY_ROLE, id: null });
  const isEditingRole = roleForm.id !== null;

  // PCP 폼
  const [pcpForm, setPcpForm] = useState({ ...EMPTY_PCP });

  useEffect(() => {
    loadOptions();
    loadPCP();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const loadOptions = async () => {
    const [cRes, rRes] = await Promise.all([
      listCompanies(),
      listRoles({ project: projectId }),
    ]);
    setCompanies(cRes.data?.results ?? cRes.data ?? []);
    setRoles(rRes.data?.results ?? rRes.data ?? []);
  };

  const loadPCP = async () => {
    setLoading(true);
    try {
      const { data } = await listPCP({ project: projectId });
      setPcpRows(data?.results ?? data ?? []);
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- 역할 등록/수정/삭제 ---------------- */
  const PRESET_ROLES = ["발주처", "CM/감리단", "설계사", "시공사"];
  const CUSTOM_LABEL = "직접 입력";
  const ROLE_CODE_MAP = {
    발주처: "OWN",
    "CM/감리단": "CM",
    설계사: "DES", // 필요 시 수정
    시공사: "CON",
  };
  const isPresetRole = (name) => PRESET_ROLES.includes(name);

  const [selectValue, setSelectValue] = useState("");
  const resetForm = () => {
    setRoleForm({ ...EMPTY_ROLE, id: null });
    setSelectValue(""); // "역할명 선택으로 초기화"
  };

  const saveRole = async () => {
    if (!roleForm.name.trim()) {
      alert("역할명을 입력하세요.");
      return;
    }
    // ✅ 새로 등록할 때, 같은 코드가 이미 있으면 서버 호출 전에 차단
    const trimmedCode = (roleForm.code || "").trim();
    if (!isEditingRole && trimmedCode) {
      const exists = roles.some((r) => (r.code || "").trim() === trimmedCode);
      if (exists) {
        alert(
          "이미 사용 중인 역할 코드입니다.\n\n다른 코드를 입력하거나 목록에서 해당 역할을 '수정'하세요."
        );
        return;
      }
    }
    try {
      if (isEditingRole) {
        await updateRole(roleForm.id, {
          code: roleForm.code || null,
          name: roleForm.name,
        });
      } else {
        await createRole({
          project: projectId,
          code: roleForm.code || null,
          name: roleForm.name,
        });
      }
      resetForm();
      await loadOptions();
    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data;
      console.error("saveRole failed:", status, msg);
      // ✅ 중복 에러 등은 메시지를 조금 더 읽기 쉽게 가공
      if (status === 400 && msg && (msg.non_field_errors || msg.code)) {
        alert(
          msg.non_field_errors?.[0] ||
            msg.code?.[0] ||
            "이미 존재하는 역할입니다. 코드나 이름을 변경해 주세요."
        );
      } else {
        alert(
          `저장 실패(${status}). ${
            typeof msg === "string" ? msg : JSON.stringify(msg)
          }`
        );
      }
    }
  };

  const editRole = (r) => {
    setRoleForm({ id: r.id, code: r.code ?? "", name: r.name ?? "" });
    setSelectValue(isPresetRole(r.name) ? r.name : CUSTOM_LABEL);
  };

  const removeRole = async (id) => {
    if (
      !window.confirm(
        "해당 역할을 삭제하시겠습니까?\n(매핑되어 있으면 실패할 수 있습니다.)"
      )
    )
      return;
    await deleteRole(id);
    await Promise.all([loadOptions(), loadPCP()]);
  };

  /* ---------------- PCP 등록/삭제 ---------------- */
  const removePcp = async (id) => {
    if (!window.confirm("이 참여사 매핑을 삭제하시겠습니까?")) return;
    await deletePCP(id);
    await loadPCP();
  };

  return (
    <div className="p-6 space-y-6">
      <h2 className="text-xl font-bold">프로젝트 참여주체(역할·매핑) 관리</h2>

      {!projectId && (
        <div className="border border-amber-300 bg-amber-50 text-amber-800 px-3 py-2 rounded">
          현재 프로젝트가 선택되지 않았습니다. 프로젝트를 먼저 선택해주세요.
        </div>
      )}

      {/* 역할 등록/관리 */}
      <fieldset className="border border-gray-300 rounded p-4">
        <legend className="text-lg font-semibold">역할 등록/관리</legend>

        {/* 입력영역 */}
        <div className="grid grid-cols-2 gap-4 mt-2">
          <div className="w-full flex items-center gap-2">
            <select
              value={selectValue}
              onChange={(e) => {
                const selected = e.target.value;
                setSelectValue(selected);

                if (selected === CUSTOM_LABEL) {
                  // 직접 입력 : input 열림, 코드 자동 세팅 해제
                  setRoleForm((v) => ({ ...v, name: "", code: "" }));
                } else if (isPresetRole(selected)) {
                  // 프리셋: 이름, 코드 자동 반영 / input 닫힘
                  setRoleForm((v) => ({
                    ...v,
                    name: selected,
                    code: ROLE_CODE_MAP[selected] || "",
                  }));
                } else {
                  // 선택하기("")
                  setRoleForm((v) => ({ ...v, name: "" }));
                }
              }}
              className="border rounded px-3 py-2 w-44"
            >
              <option value="" disabled>
                역할명 선택 *
              </option>
              {PRESET_ROLES.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
              <option value={CUSTOM_LABEL}>{CUSTOM_LABEL}</option>
            </select>

            {/* 직접 입력일 때만 오른쪽 인풋 노출 */}
            {selectValue === CUSTOM_LABEL && (
              <input
                type="text"
                placeholder="역할명 * 예: CM단 / 발주처 / 시공사"
                value={roleForm.name}
                onChange={(e) =>
                  setRoleForm((v) => ({ ...v, name: e.target.value }))
                }
                className="border rounded px-3 py-2 flex-1"
              />
            )}
          </div>
          <input
            type="text"
            placeholder={
              roleForm.name
                ? "코드(선택) 예: CM, OWN, CON"
                : "역할을 먼저 선택해 주세요."
            }
            value={roleForm.code}
            onChange={(e) =>
              setRoleForm((v) => ({ ...v, code: e.target.value }))
            }
            readOnly={!roleForm.name}
            className="border rounded px-3 py-2 w-full"
          />
        </div>

        <div className="mt-4 flex gap-2">
          {isEditingRole && (
            <button
              onClick={() => setRoleForm({ ...EMPTY_ROLE, id: null })}
              className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded"
            >
              취소
            </button>
          )}
          <button
            onClick={saveRole}
            className={`${
              isEditingRole
                ? "bg-green-500 hover:bg-green-600"
                : "bg-blue-500 hover:bg-blue-600"
            } text-white px-4 py-2 rounded`}
          >
            {isEditingRole ? "수정" : "등록"}
          </button>
        </div>

        {/* 목록 */}
        <div className="overflow-x-auto mt-6">
          <table className="min-w-full border border-gray-300 text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="border px-2 py-1 w-16">ID</th>
                <th className="border px-2 py-1 w-40">코드</th>
                <th className="border px-2 py-1">역할명</th>
                <th className="border px-2 py-1 w-40">관리</th>
              </tr>
            </thead>
            <tbody>
              {roles.map((r) => (
                <tr key={r.id} className="hover:bg-gray-50">
                  <td className="border px-2 py-1 text-center">{r.id}</td>
                  <td className="border px-2 py-1">{r.code ?? "-"}</td>
                  <td className="border px-2 py-1">{r.name}</td>
                  <td className="border px-2 py-1">
                    <div className="flex gap-2">
                      <button
                        onClick={() => editRole(r)}
                        className="bg-yellow-500 hover:bg-yellow-600 text-white px-2 py-1 rounded"
                      >
                        수정
                      </button>
                      <button
                        onClick={() => removeRole(r.id)}
                        className="bg-red-500 hover:bg-red-600 text-white px-2 py-1 rounded"
                      >
                        삭제
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {roles.length === 0 && (
                <tr>
                  <td colSpan={4} className="text-center py-4">
                    역할이 없습니다.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </fieldset>

      {/* PCP 목록 */}
      <div className="overflow-x-auto">
        <table className="min-w-full border border-gray-300 text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="border px-2 py-1 w-16">ID</th>
              <th className="border px-2 py-1">회사</th>
              <th className="border px-2 py-1">역할</th>
              <th className="border px-2 py-1 w-20">주관</th>
              <th className="border px-2 py-1 w-48">기간</th>
              <th className="border px-2 py-1 w-28">관리</th>
            </tr>
          </thead>
          <tbody>
            {pcpRows.map((row) => {
              const comp = row.company_name ?? row.company?.name ?? row.company;
              const role =
                row.role_name ??
                row.participant_role?.name ??
                row.participant_role;
              const period = `${row.start_date ?? "-"} ~ ${
                row.end_date ?? "-"
              }`;
              return (
                <tr key={row.id} className="hover:bg-gray-50">
                  <td className="border px-2 py-1 text-center">{row.id}</td>
                  <td className="border px-2 py-1">{comp}</td>
                  <td className="border px-2 py-1">{role}</td>
                  <td className="border px-2 py-1 text-center">
                    {row.is_lead ? "Y" : ""}
                  </td>
                  <td className="border px-2 py-1">{period}</td>
                  <td className="border px-2 py-1">
                    <div className="flex gap-2">
                      <button
                        onClick={() => removePcp(row.id)}
                        className="bg-red-500 hover:bg-red-600 text-white px-2 py-1 rounded"
                      >
                        삭제
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {pcpRows.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center py-4">
                  {loading ? "불러오는 중..." : "데이터 없음"}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default User_pcp;
